import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fmtMoney } from "../../lib/format";
import { fmtDurationSeconds } from "../../lib/dashboardDates";
import OwnerBadge from "../OwnerBadge";

/**
 * Per-owner performance table (Резултати по търговец).
 *
 * @param {Object[]} rows - rows returned by GET /api/dashboard/by-owner
 * @param {(uid: string|null)=>void} onRowClick - opens the filtered
 *   quotations list. ``uid = null`` → unassigned filter.
 */
export default function DashboardByOwner({ rows, loading, onRowClick }) {
  const { t } = useTranslation();

  const displayRows = useMemo(() => rows || [], [rows]);
  const anyMixed = displayRows.some((r) => r.mixed_currency);

  if (loading) {
    return <div className="qw-skel h-280 stretch" aria-busy="true" />;
  }
  if (!displayRows.length) {
    return (
      <div className="qw-empty" data-testid="by-owner-empty">
        {t("dashboard.by_owner_empty")}
      </div>
    );
  }

  return (
    <div>
      <div className="qw-table-wrap" tabIndex={0} role="region" aria-label={t("dashboard.section_by_owner")}>
        <table className="qw-table qw-by-owner-table" data-testid="by-owner-table">
        <thead>
          <tr>
            <th>{t("dashboard.by_owner_column_owner")}</th>
            <th style={{ textAlign: "right" }}>{t("dashboard.by_owner_column_sent")}</th>
            <th style={{ textAlign: "right" }}>{t("dashboard.by_owner_column_opened")}</th>
            <th style={{ textAlign: "right" }}>{t("dashboard.by_owner_column_responded")}</th>
            <th style={{ textAlign: "right" }}>{t("dashboard.by_owner_column_accepted")}</th>
            <th style={{ textAlign: "right" }}>{t("dashboard.by_owner_column_declined")}</th>
            <th style={{ textAlign: "right" }}>{t("dashboard.by_owner_column_accepted_value")}</th>
            <th style={{ textAlign: "right" }}>{t("dashboard.by_owner_column_acceptance_rate")}</th>
            <th style={{ textAlign: "right" }}>{t("dashboard.by_owner_column_avg_response")}</th>
            <th style={{ textAlign: "right" }}>{t("dashboard.by_owner_column_awaiting")}</th>
          </tr>
        </thead>
        <tbody>
          {displayRows.map((r) => (
            <tr
              key={r.owner_user_id || "unassigned"}
              data-clickable="true"
              onClick={() => onRowClick(r.owner_user_id)}
              data-testid={`by-owner-row-${r.owner_user_id || "unassigned"}`}
            >
              <td>
                {r.owner ? (
                  <OwnerBadge owner={r.owner} small />
                ) : r.owner_user_id === null ? (
                  <span
                    className="qw-owner-pill unassigned sm"
                    data-testid="by-owner-unassigned-badge"
                  >
                    {t("dashboard.unassigned_row_label")}
                  </span>
                ) : (
                  <span style={{ color: "#94a3b8" }}>—</span>
                )}
              </td>
              <td style={{ textAlign: "right" }}>{r.sent_count}</td>
              <td style={{ textAlign: "right" }}>{r.opened_count}</td>
              <td style={{ textAlign: "right" }}>{r.responded_count}</td>
              <td style={{ textAlign: "right" }}>{r.accepted_count}</td>
              <td style={{ textAlign: "right" }}>{r.declined_count}</td>
              <td style={{ textAlign: "right" }}>
                {fmtMoney(r.accepted_value, "EUR")}
                {r.mixed_currency && (
                  <span
                    className="qw-by-owner-mixed"
                    title={t("dashboard.by_owner_mixed_currency")}
                    data-testid={`by-owner-mixed-${r.owner_user_id || "unassigned"}`}
                  >
                    ⚠
                  </span>
                )}
              </td>
              <td style={{ textAlign: "right" }}>
                {(r.acceptance_rate_pct || 0).toFixed(1)}%
              </td>
              <td style={{ textAlign: "right" }}>
                {fmtDurationSeconds(r.avg_seconds_to_response, t)}
              </td>
              <td style={{ textAlign: "right" }}>{r.awaiting_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div className="qw-by-owner-disclaimer">
        {t("dashboard.by_owner_disclaimer")}
        {anyMixed && (
          <span style={{ display: "block", color: "#b45309", marginTop: 4 }}>
            {t("dashboard.by_owner_mixed_currency")}
          </span>
        )}
      </div>
    </div>
  );
}
